import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import {
  convertDecimalHeightToFeetInch,
  gettextGender,
  checkToFormatPhoneNumber,
  formatPhoneNumber,
  generateFullAddress,
  generateFullName,
} from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';

/**
 * Builds the sectioned label-value data behind the patient info drawer of a study. Assembles patient information with the height converted to feet and inches and the weight to one decimal, patient contact and an optional secondary contact whose phone numbers are formatted only for countries that need it, insurance, and medical history listing symptoms and their frequency as separate lines, with a placeholder for missing values.
 *
 * @param study - Study whose patient info, onboarding and insurance data are read.
 * @returns The drawer sections, each with a header and its label-value content.
 */
export type PatientInfoSection = {
  header: string;
  content: { label: string; value: any; className?: string }[];
};

/**
 * Builds the sections of label-value rows shown in a study's patient info drawer. Reads patient,
 * contact, optional secondary contact, insurance and medical history fields from the study info and
 * onboarding, formatting height, weight and phone numbers and falling back to a double-hyphen
 * placeholder for missing values.
 *
 * @param study - Study whose patient info, onboarding and insurance data are read.
 * @returns The five drawer sections, the secondary contact one undefined when it has no data.
 */
export const generatePatientInfoData = (study: any) => {
  const { info, onboarding } = study || {};
  const heightFormat = convertDecimalHeightToFeetInch(info?.patient?.height);
  const shouldFormatPhoneNumber = checkToFormatPhoneNumber(info?.patient?.contact?.country);
  const list: [
    PatientInfoSection,
    PatientInfoSection,
    PatientInfoSection | undefined,
    PatientInfoSection,
    PatientInfoSection,
  ] = [
    {
      header: t`Patient information`,
      content: [
        {
          label: t`Name`,
          value: generateFullName(info?.patient),
        },
        {
          label: t`Gender`,
          value: gettextGender(info?.patient?.gender) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`ID`,
          value: info?.patient?.patientId || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`DOB`,
          value: info?.patient?.dob || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Height`,
          value: !_.isEmpty(heightFormat)
            ? `${heightFormat.ft} ft ${heightFormat.ins} in`
            : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Weight`,
          value: info?.patient?.weight
            ? `${parseFloat(info?.patient.weight).toFixed(1)} lb`
            : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Ethnicity`,
          value: info?.patient?.ethnicity || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Pacemaker`,
          value: onboarding?.pacer || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`ICD`,
          value: onboarding?.icd ? 'Yes' : 'No',
        },
      ],
    },
    {
      header: t`Patient contact`,
      content: [
        {
          label: t`Address`,
          value: generateFullAddress({ contact: info?.patient?.contact }),
        },
        {
          label: t`Zip code`,
          value: info?.patient?.contact?.zip || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Phone number`,
          value:
            (shouldFormatPhoneNumber
              ? formatPhoneNumber(info?.patient?.contact?.phone1)
              : info?.patient?.contact?.phone1) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`2nd phone number`,
          value:
            (shouldFormatPhoneNumber
              ? formatPhoneNumber(info?.patient?.contact?.phone2)
              : info?.patient?.contact?.phone2) || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Email`,
          value: info?.patient?.email || DOUBLE_HYPHEN_BULLET,
        },
      ],
    },
    info?.patient?.secondaryContact?.name ||
    info?.patient?.secondaryContact?.phone ||
    info?.patient?.secondaryContact?.relationship ||
    info?.patient?.secondaryContact?.email
      ? {
          header: t`Secondary contact`,
          content: [
            {
              label: t`Name`,
              value: info?.patient?.secondaryContact?.name || DOUBLE_HYPHEN_BULLET,
            },
            {
              label: t`Phone Number`,
              value:
                (shouldFormatPhoneNumber
                  ? formatPhoneNumber(info?.patient?.secondaryContact?.phone)
                  : info?.patient?.secondaryContact?.phone) || DOUBLE_HYPHEN_BULLET,
            },
            {
              label: t`Relationship`,
              value: info?.patient?.secondaryContact?.relationship || DOUBLE_HYPHEN_BULLET,
            },
            {
              label: t`Email`,
              value: info?.patient?.secondaryContact?.email || DOUBLE_HYPHEN_BULLET,
            },
          ],
        }
      : undefined,
    {
      header: t`Insurance`,
      content: [
        {
          label: t`Insurance type`,
          value: info?.insurance?.type || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Provider`,
          value: info?.insurance?.provider || DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Pre-authorization required`,
          value: info?.insurance?.preAuth === 'true' ? 'Yes' : 'No',
        },
      ],
    },
    {
      header: t`Medical history`,
      content: [
        {
          label: t`Symptoms the patient experienced`,
          value:
            onboarding?.patientSymptoms?.length > 0
              ? _.map(onboarding?.patientSymptoms, (item) => (
                  <span style={{ display: 'block' }}>{item}</span>
                ))
              : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Symptom frequency`,
          value:
            onboarding?.patientSymptoms?.length > 0 && onboarding?.frequentlyOfSymptoms?.length > 0
              ? _.map(onboarding?.frequentlyOfSymptoms, (item) =>
                  item ? <span style={{ display: 'block' }}>{item}</span> : DOUBLE_HYPHEN_BULLET,
                )
              : DOUBLE_HYPHEN_BULLET,
        },
        {
          label: t`Used heart monitor`,
          value:
            onboarding?.patientSymptoms?.length > 0 && _.isBoolean(onboarding?.usedHeartMonitor)
              ? onboarding?.usedHeartMonitor
                ? 'Yes'
                : 'No'
              : DOUBLE_HYPHEN_BULLET,
        },
      ],
    },
  ];
  return list;
};
